import React, { useMemo, useState } from 'react';
import { Table, Icon, Button, Pagination, Breadcrumb, Checkbox, Message } from 'semantic-ui-react';

const ItemsTable = ({
                        column,
                        direction,
                        handleSort,
                        handleSelectAll,
                        selectAll,
                        selectAllIndeterminate = false,
                        TableContentComponent,
                        filteredItems = [], // Default value for filteredItems
                        selectedItems,
                        setSelectedItems,
                        selectedListingIds,
                        setSelectedListingIds,
                        publishSelectionMode,
                        firstCopyGuidanceActive = false,
                        onFirstCopyGuidanceDismiss,
                        autoExpandListingIds,
                        handleEditClick,
                        handleViewClick,
                        onViewKit,
                        rowEditEnabled,
                        handleInlineListingUpdate,
                        handleDeleteListing,
                        listingFinancialsById,
                        listingFinancialsLoading,
                        handleViewFinancialDetail,
                        bulkPriceUpdateEnabled = false,
                        packsEnabled = false,
                        serverPagination = false,
                        serverPage = 1,
                        serverTotalPages = 1,
                        serverTotalItems = 0,
                        onServerPageChange,
                        serverPageSize = 20,
                        onServerPageSizeChange,
                        emptyMessage,
                    }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(() => {
        const savedItemsPerPage = localStorage.getItem('itemsPerPage');
        return savedItemsPerPage ? parseInt(savedItemsPerPage, 10) : 10;
    });

    const totalPages = serverPagination
      ? Math.max(1, Number(serverTotalPages || 1))
      : Math.ceil((filteredItems?.length || 0) / itemsPerPage); // Safely access length

    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = serverPagination
      ? (filteredItems || [])
      : filteredItems.slice(startIndex, startIndex + itemsPerPage);
    const hasRows = currentItems.length > 0;
    const tableContentArgs = useMemo(
      () => ({
        filteredItems: currentItems,
        selectedItems,
        setSelectedItems,
        selectedListingIds,
        setSelectedListingIds,
        publishSelectionMode,
        firstCopyGuidanceActive,
        onFirstCopyGuidanceDismiss,
        autoExpandListingIds,
        handleEditClick,
        handleViewClick,
        onViewKit,
        rowEditEnabled,
        handleInlineListingUpdate,
        handleDeleteListing,
        listingFinancialsById,
        listingFinancialsLoading,
        handleViewFinancialDetail,
        bulkPriceUpdateEnabled,
        packsEnabled,
      }),
      [
        currentItems,
        selectedItems,
        setSelectedItems,
        selectedListingIds,
        setSelectedListingIds,
        publishSelectionMode,
        firstCopyGuidanceActive,
        onFirstCopyGuidanceDismiss,
        autoExpandListingIds,
        handleEditClick,
        handleViewClick,
        onViewKit,
        rowEditEnabled,
        handleInlineListingUpdate,
        handleDeleteListing,
        listingFinancialsById,
        listingFinancialsLoading,
        handleViewFinancialDetail,
        bulkPriceUpdateEnabled,
        packsEnabled,
      ],
    );

    const handlePaginationChange = (e, { activePage }) => {
        const nextPage = Number(activePage);
        if (!Number.isFinite(nextPage) || nextPage < 1) return;
        if (serverPagination) {
            if (typeof onServerPageChange === 'function') onServerPageChange(nextPage);
            return;
        }
        setCurrentPage(nextPage);
    };

    const handleItemsPerPageChange = (value) => {
        if (serverPagination) {
            if (typeof onServerPageSizeChange === 'function') onServerPageSizeChange(value);
            return;
        }
        setItemsPerPage(value);
        localStorage.setItem('itemsPerPage', String(value));  // Save to localStorage
        setCurrentPage(1); // Reset to first page on items per page change
    };

    const itemsPerPageOptions = [
        { key: 10, value: 10, text: '10' },
        { key: 20, value: 20, text: '20' },
        { key: 50, value: 50, text: '50' },
        { key: 100, value: 100, text: '100' },
    ];

    return (
        <>
<div
    className="responsive-table-container publications-table-scroll"
    style={{
        paddingBottom: '8px',
        borderRadius: '14px',
        background: 'linear-gradient(180deg, rgba(248, 250, 252, 0.96) 0%, rgba(241, 245, 249, 0.96) 100%)',
        boxShadow: 'inset 0 -1px 0 rgba(148, 163, 184, 0.22)',
    }}
>
<Table celled striped selectable style={{ minWidth: '1100px', marginBottom: 0 }}>
    <Table.Header>
        <Table.Row>
            <Table.HeaderCell style={{ textAlign: 'center', verticalAlign: 'middle', padding: '5px', width: '50px' }}>
                <Checkbox
                    aria-label="Seleccionar publicaciones visibles"
                    checked={selectAll}
                    indeterminate={selectAllIndeterminate}
                    disabled={!hasRows}
                    onChange={handleSelectAll}
                />
            </Table.HeaderCell>
            <Table.HeaderCell
                sorted={column === 'title' ? direction : null}
                onClick={() => handleSort('title')}
                style={{
                    justifyContent: 'start',
                    alignItems: 'center',
                    width: '300px',
                    minWidth: '300px',
                    maxWidth: '300px',  // Añadir estos dos estilos
                }}
            >
                Título
                {column === 'title' && <Icon name={direction === 'ascending' ? 'arrow up' : 'arrow down'} />}
            </Table.HeaderCell>
            <Table.HeaderCell
                sorted={column === 'variationName' ? direction : null}
                onClick={() => handleSort('variationName')}
                style={{
                    justifyContent: 'start',
                    alignItems: 'center',
                    width: '120px',
                    minWidth: '120px',
                    maxWidth: '120px',  // Añadir estos dos estilos
                }}
            >
                Variation
                {column === 'variationName' && <Icon name={direction === 'ascending' ? 'arrow up' : 'arrow down'} />}
            </Table.HeaderCell>
            <Table.HeaderCell
                sorted={column === 'sku' ? direction : null}
                onClick={() => handleSort('sku')}
                style={{
                    justifyContent: 'start',
                    alignItems: 'center',
                    width: '120px',
                    minWidth: '120px',
                    maxWidth: '120px',  // Añadir estos dos estilos
                }}
            >
                SKU
                {column === 'sku' && <Icon name={direction === 'ascending' ? 'arrow up' : 'arrow down'} />}
            </Table.HeaderCell>
            <Table.HeaderCell
                sorted={column === 'cost' ? direction : null}
                onClick={() => handleSort('cost')}
                style={{
                    justifyContent: 'start',
                    alignItems: 'center',
                    width: '100px',
                    minWidth: '100px',
                    maxWidth: '100px',
                }}
            >
                Costo
                {column === 'cost' && <Icon name={direction === 'ascending' ? 'arrow up' : 'arrow down'} />}
            </Table.HeaderCell>
            <Table.HeaderCell
                sorted={column === 'status' ? direction : null}
                onClick={() => handleSort('status')}
                style={{
                    justifyContent: 'start',
                    alignItems: 'center',
                    width: '90px',
                    minWidth: '90px',
                    maxWidth: '90px',  // Añadir estos dos estilos
                }}
            >
                Estado
                {column === 'status' && <Icon name={direction === 'ascending' ? 'arrow up' : 'arrow down'} />}
            </Table.HeaderCell>
            <Table.HeaderCell
                sorted={column === 'marketplace' ? direction : null}
                onClick={() => handleSort('marketplace')}
                style={{
                    justifyContent: 'start',
                    alignItems: 'center',
                    width: '50px',
                    minWidth: '50px',
                    maxWidth: '50px',  // Añadir estos dos estilos
                }}
            >
                
                {column === 'marketplace' && <Icon name={direction === 'ascending' ? 'arrow up' : 'arrow down'} />}
            </Table.HeaderCell>
            <Table.HeaderCell style={{ width: '76px' }}></Table.HeaderCell>
        </Table.Row>
    </Table.Header>

    {hasRows ? (
        <TableContentComponent
            args={tableContentArgs}
        />
    ) : (
        <Table.Body>
            <Table.Row>
                <Table.Cell colSpan="8">
                    <Message info style={{ margin: 0 }}>
                        {emptyMessage || 'No hay publicaciones para mostrar con los filtros actuales.'}
                    </Message>
                </Table.Cell>
            </Table.Row>
        </Table.Body>
    )}
    <Table.Footer>
        <Table.Row>
            <Table.HeaderCell colSpan="8">
                <div className="ui small label">
                    <i className="file alternate outline icon"></i>
                    Páginas
                </div>
                <Breadcrumb>
                    {itemsPerPageOptions.map((option, index) => (
                        <React.Fragment key={option.key}>
                            <Breadcrumb.Section
                                active={(serverPagination ? serverPageSize : itemsPerPage) === option.value}
                                onClick={() => handleItemsPerPageChange(option.value)}
                                style={{ cursor: 'pointer', color: 'purple' }}
                            >
                                {option.text}
                            </Breadcrumb.Section>
                            {index < itemsPerPageOptions.length - 1 && <Breadcrumb.Divider>/</Breadcrumb.Divider>}
                        </React.Fragment>
                    ))}
                </Breadcrumb>
            </Table.HeaderCell>
        </Table.Row>
    </Table.Footer>
</Table>
</div>
<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px', color: '#64748b', fontSize: '12px' }}>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <Icon name="arrows alternate horizontal" />
        Deslizá la tabla para ver más columnas
    </span>
</div>
<div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
    <Pagination
        activePage={serverPagination ? serverPage : currentPage}
        onPageChange={handlePaginationChange}
        totalPages={totalPages}
        ellipsisItem={null}
        firstItem={null}
        lastItem={null}
        siblingRange={1}
        color="purple"
    />
</div>
{serverPagination ? (
    <div style={{ textAlign: 'center', marginTop: '8px', color: '#666' }}>
        Pagina {serverPage} de {totalPages} ({serverTotalItems} registros)
    </div>
) : null}



        </>
    );
};

export default React.memo(ItemsTable);
